import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';
import { Download, ExternalLink, Image as ImageIcon, Mail, Share2 } from 'lucide-react';

const ASSETS = [
  {
    id: 'logo-pack',
    icon: ImageIcon,
    title: 'Logo Pack',
    description:
      'Print-quality PNGs (4000px) — horizontal and stacked layouts, light and dark versions, transparent and solid backgrounds. For business cards, promotional items, and print.',
    href: '/downloads/NWTA-logo-pack.zip',
    action: 'Download ZIP',
    meta: '8 files · PNG',
  },
  {
    id: 'art-logos',
    icon: ImageIcon,
    title: 'ART Logos',
    description:
      'The final ART (AI Rendered Title) brand set — the tree artwork, plus horizontal logos in light and navy versions for documents, decks, and print.',
    href: '/downloads/NWTA-art-logos.zip',
    action: 'Download ZIP',
    meta: '3 files · PNG',
  },
  {
    id: 'axiom-edition-logos',
    icon: ImageIcon,
    title: 'AXIOM Edition Logos',
    description:
      'Final logos for the AXIOM editions — Underwriter, Lenders, Attorneys, National Agents, and Commercial — transparent PNGs for decks, documents, and product materials.',
    href: '/downloads/NWTA-axiom-edition-logos.zip',
    action: 'Download ZIP',
    meta: '5 files · PNG',
  },
  {
    id: 'social-banners',
    icon: Share2,
    title: 'Social Banners',
    description:
      'Cover images sized for each platform: Facebook Page (1640×624), LinkedIn Company Page (2256×382), and LinkedIn personal profile (1584×396, logo offset for your photo).',
    href: '/downloads/NWTA-social-banners.zip',
    action: 'Download ZIP',
    meta: '3 files · PNG',
  },
  {
    id: 'business-cards',
    icon: ImageIcon,
    title: 'Business Card Generator',
    description:
      'Enter your name, title, email, and phone — download print-ready front and back files (300 DPI with bleed). The back carries a QR code that saves your contact card when scanned.',
    href: '/downloads/business-card.html',
    action: 'Open generator',
    external: true,
    meta: 'Web tool',
  },
  {
    id: 'signature-generator',
    icon: Mail,
    title: 'Email Signature Generator',
    description:
      'Type your name, title, email, and phone — then copy a fully formatted signature into Gmail, Outlook, or Apple Mail. Always copy from this live page so the logo link stays permanent.',
    href: '/downloads/email-signature.html',
    action: 'Open generator',
    external: true,
    meta: 'Web tool',
  },
  {
    id: 'signature-kit',
    icon: Download,
    title: 'Email Signature Kit (offline)',
    description:
      'The same generator as a downloadable ZIP with the logo file and step-by-step install instructions for each email client — handy to send to teammates.',
    href: '/downloads/NWTA-email-signature.zip',
    action: 'Download ZIP',
    meta: 'ZIP · HTML + PNG + README',
  },
];

const AssetCard = ({ asset }) => {
  const Icon = asset.icon;
  return (
    <div
      className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all"
      data-testid={`brand-asset-${asset.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-[#F1F4F8] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#3E5C76]" />
        </div>
        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-400">{asset.meta}</span>
      </div>
      <h2 className="mt-5 font-serif text-xl font-semibold text-[#0B2545] tracking-tight">{asset.title}</h2>
      <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600 flex-1">{asset.description}</p>
      <a
        href={asset.href}
        {...(asset.external ? { target: '_blank', rel: 'noreferrer' } : { download: true })}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B2545] hover:bg-[#13315C] text-white text-[13.5px] font-medium px-5 py-2.5 transition-all shadow-sm hover:shadow-md"
        data-testid={`brand-asset-link-${asset.id}`}
      >
        {asset.external ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
        {asset.action}
      </a>
    </div>
  );
};

export default function BrandPage() {
  usePageMeta(
    'Brand Assets — Northwest Title Alliance',
    'Internal brand asset library: logos, social banners, and email signatures for the Northwest Title Alliance team.'
  );

  return (
    <div className="min-h-screen bg-[#F6F8FA]">
      <Header />
      <main className="pt-32 pb-24" data-testid="brand-page">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">Team resources</div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-medium tracking-[-0.02em] text-[#0B2545]">
              Brand Assets
            </h1>
            <p className="mt-4 text-[15.5px] leading-relaxed text-slate-600">
              Everything the team needs to represent Northwest Title Alliance — logos for print and
              digital, platform-sized social covers, and ready-to-paste email signatures.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {ASSETS.map((a) => (
              <AssetCard key={a.id} asset={a} />
            ))}
          </div>
          <p className="mt-8 text-[12.5px] text-slate-400">
            Brand colors: deep navy #0B2545 · steel blue #3E5C76 · mist #8DA9C4 · fog white #F6F8FA — fonts: Fraunces &amp; Inter.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
